import React, { forwardRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Body, DefaultIcon, ErrorIcon, InfoIcon, Root, Subtitle, SuccessIcon, Title, WarningIcon } from './Toast.styles'

/**
 * The Toast's component.
 */
const Toast = forwardRef((props, ref) => {
  const {
    align,
    border,
    children,
    disableBackground,
    disableIcon,
    margin,
    padding,
    severity,
    textDirection,
    title,
    width,
    ...restProps
  } = props

  const BaseIcon = useMemo(() => {
    switch (severity) {
      case 'error':
        return ErrorIcon
      case 'info':
      case 'primary':
        return InfoIcon
      case 'success':
        return SuccessIcon
      case 'warning':
        return WarningIcon
      case 'default':
      default:
        return DefaultIcon
    }
  }, [severity])

  return (
    <Root
      {...restProps}
      align={align}
      border={border ? 1 : 0}
      disableBackground={disableBackground ? 1 : 0}
      icon={disableIcon ? false : <BaseIcon />}
      margin={margin}
      padding={padding}
      ref={ref}
      severity={severity}
      width={width}
    >
      <>
        {title && <Title>{title}</Title>}
        {textDirection === 'horizontal' && <Subtitle>{children}</Subtitle>}
      </>
      {textDirection === 'vertical' && <Body>{children}</Body>}
    </Root>
  )
})

Toast.defaultProps = {
  align: 'left',
  border: false,
  disableBackground: false,
  disableIcon: false,
  severity: 'info',
  textDirection: 'horizontal',
  width: 'auto'
}

Toast.propTypes = {
  /**
   * Align.
   */
  align: PropTypes.oneOf(['left', 'center', 'right', 'justify']),
  /**
   * Border.
   */
  border: PropTypes.bool,
  /**
   * Children.
   */
  children: PropTypes.node.isRequired,
  /**
   * Disable background.
   */
  disableBackground: PropTypes.bool,
  /**
   * Disable icon.
   */
  disableIcon: PropTypes.bool,
  /**
   * Margin.
   */
  margin: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Padding.
   */
  padding: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Severity.
   */
  severity: PropTypes.oneOf(['default', 'error', 'info', 'primary', 'success', 'warning']).isRequired,
  /**
   * Text direction.
   */
  textDirection: PropTypes.oneOf(['horizontal', 'vertical']),
  /**
   * Title.
   */
  title: PropTypes.string,
  /**
   * Width.
   */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
}

export default Toast
